// roll-audio-mixer.ts — layered combat SFX playback through the soundboard players.
//
// Each roll FRAME plays its OWN sound, spread across the EXISTING DOM <audio> soundboard players
// (audio_player_effect_1..N) as a round-robin pool — so concurrent sounds (cast + damage + status) land on
// different players and layer.
//
// The browser CHOOSES the sound (owner 2026-09-10). A frame carries only `roll_sound`, a reference ({v:2, rev,
// used?}); playRollSound() builds the route here from the frame's ids, the encounter's `.catalog` item refs and the
// catalog's rules (shared/roll-sound-route.ts), then picks the take from the versioned audio catalog
// (shared/audio-catalog.ts), seeded by the row so every client hears the same one. Secret rows carry no reference
// (silent, as before).
//
// We use the real DOM players (NOT dynamic `new Audio()`) on purpose: they're already unlocked for autoplay
// and carry the user's per-channel volume/mute from the soundboard. Add more <audio id="audio_player_effect_3">
// etc. and the pool picks them up automatically (more simultaneous layers).
//
// Channel reservation: a channel is reserved when a sound is assigned to it and freed when that sound
// ACTUALLY ends ('ended'/'error'), NOT after a fixed timer. A safety timeout still frees a channel if a load
// silently stalls.
//
// Diagnostics go through ftDebugLog('audio', …) — silent unless that debug channel is enabled.

import { actionSoundTakes, loadAudioCatalog, peekAudioCatalog, resolveRollSound, rollSound, warmVoiceShards } from '../shared/audio-catalog';
import { captureRollSoundContext, encounterVoiceSlugs, pageRollSoundContext, rollSoundRoute } from '../shared/roll-sound-route';
import {castSoundAt, projectileImpactMs, rollPresentationTiming} from '../shared/spell-cast-timing';
import { effectTake, MAX_EFFECT_PREROLL_SECONDS, type EffectTake } from './effect-onset';
import { attackImpactAt } from '../shared/attack-impact-clock';
import { embeddedCampaignAudioSuppressed, ftCampaignAudioSurface, ftIsViewersOwn } from '../shared/audio-surface';
import { installFootstepAudioOwner } from '../encounter-creation-engine/ft3d-footsteps';
import { castCommitId } from '../janus-engine/presentation/cast-commit';

type RollAudioMixer = {
    reserveEffectDevice(stop: () => void): { player: HTMLAudioElement; evicted: boolean; release: () => void } | null;
    playChatVoiceLine(url: string): void;
    playCampaignCue(url: string): void;
    playRollSound(roll: Record<string, unknown> | undefined): Promise<void>;
    playActionSound(operationId: string, sound: unknown, opts?: { peer?: boolean; hitAt?: number }): Promise<void>;
};

// All importing bundles call the page's first mixer. Only that owner installs devices
// and listeners; actor commits and WebSocket echoes share the same event identities.
const host = window as Window & { ftRollAudioMixer?: RollAudioMixer };
export const { reserveEffectDevice, playChatVoiceLine, playCampaignCue, playRollSound, playActionSound }
    = host.ftRollAudioMixer ??= createRollAudioMixer();

function createRollAudioMixer(): RollAudioMixer {
    installFootstepAudioOwner();

    const MAX_RESERVE_MS = 10000; // safety net: never lock a channel longer than this, even if
                                  // 'ended' never fires (stalled load / autoplay-blocked play()).
    const LATE_MS = 2500; // a sound that could not be resolved within this long after its frame is dropped:
                          // a cue that lands seconds after the event reads as a bug, not as feedback.

    const reserved = new Map<HTMLAudioElement, number>(); // el -> active reservation token (busy iff present)
    const effectStops = new Map<HTMLAudioElement, () => void>();
    let reserveSeq = 0;
    let lastPlayAt = 0; // for inter-frame timing in the debug trace

    function effectPlayers(): HTMLAudioElement[] {
        const out: HTMLAudioElement[] = [];
        for (let i = 1; i <= 8; i++) {
            const el = document.getElementById('audio_player_effect_' + i) as HTMLAudioElement | null;
            if (el) out.push(el); else break;
        }
        return out;
    }

    function isBusy(p: HTMLAudioElement): boolean {
        return reserved.has(p);
    }

    // Prefer a free channel; if every channel is genuinely busy (real over-subscription),
    // evict the one closest to finishing — the only case where we cut a live sound.
    function pickPlayer(players: HTMLAudioElement[]): { player: HTMLAudioElement; evicted: boolean } {
        for (const p of players) { if (!isBusy(p)) return { player: p, evicted: false }; }
        let best = players[0]!;
        let bestRemaining = Infinity;
        for (const p of players) {
            const remaining = (p.duration || 0) - (p.currentTime || 0);
            if (remaining < bestRemaining) { bestRemaining = remaining; best = p; }
        }
        return { player: best, evicted: true };
    }

    // Reserve + play. The reservation is released by the sound's own lifecycle, so a
    // channel stays "busy" for as long as its sound is actually playing — load time included.
    function playOn(player: HTMLAudioElement, url: string, seekSeconds = 0): void {
        effectStops.get(player)?.();
        const token = ++reserveSeq;
        reserved.set(player, token);
        const free = () => { if (reserved.get(player) === token) reserved.delete(player); };
        player.addEventListener('ended', free, { once: true });
        player.addEventListener('error', free, { once: true });
        // A stalled load must not lock the channel forever.
        setTimeout(free, MAX_RESERVE_MS);
        try {
            player.src = url;
            let started = false;
            const start = () => {
                if (started || reserved.get(player) !== token || player.readyState < 2) return;
                started = true;
                player.onloadeddata = null;
                if (seekSeconds > 0) player.currentTime = seekSeconds;
                void player.play().catch(() => { /* autoplay/abort — ignore */ });
            };
            // Start only after this load is ready. Do not request playback again on
            // loadeddata; an evicted reservation must not start at all.
            player.onloadeddata = start;
            player.load();
            start();
        } catch { free(); }
    }

    /** Shared soundboard devices; dedicated effects supply an event API, never a fabricated roll. */
    function reserveEffectDevice(stop: () => void): { player: HTMLAudioElement; evicted: boolean; release: () => void } | null {
        if (embeddedCampaignAudioSuppressed()) return null;
        const players = effectPlayers().filter(p => !p.muted && p.volume > 0);
        if (!players.length) return null;
        const { player, evicted } = pickPlayer(players);
        effectStops.get(player)?.();
        player.pause();
        const token = ++reserveSeq;
        reserved.set(player, token); effectStops.set(player, stop);
        return { player, evicted, release: () => {
            if (reserved.get(player) === token) { reserved.delete(player); effectStops.delete(player); }
        } };
    }

    // VoiceForge: a row's spoken line goes to the soundboard's DEDICATED Voice channel when that element exists
    // (sidelog.php): dialogue carries meaning the effect layers do not, so it gets its own level, its own mute,
    // and immunity from the effect pool's round-robin eviction. Muted/zeroed is a deliberate user choice — never
    // reroute dialogue onto an effect channel to get around it. Without the channel it falls back to the pool.
    /** Bumped by every voice line started, so a queued second line can tell whether the channel changed hands. */
    let voiceGeneration = 0;
    function playVoice(url: string): void {
        voiceGeneration++;
        const voiceChannel = document.getElementById('audio_player_voice_1') as HTMLAudioElement | null;
        if (voiceChannel && !voiceChannel.muted && voiceChannel.volume > 0) {
            ftDebugLog('audio', { phase: 'play', file: url, type: 'voice', chosenChannelId: voiceChannel.id });
            playOn(voiceChannel, url);
        } else if (voiceChannel) {
            ftDebugLog('audio', { phase: 'play', file: url, type: 'voice', skipReason: 'voice-channel-muted' });
        } else {
            playEffect(url);
        }
    }

    /**
     * Two lines on ONE voice channel, the second when the first ends (a stealth spot: "Found you." then "Cover is blown.").
     * The channel is a single element, so starting both at once would cut the first.
     *   - Ownership is the VOICE GENERATION, not the reservation: if any other line started after the first, the pair was
     *     displaced and the second is dropped — even when that other line has since finished and freed the channel.
     *   - The watchdog never cuts a line that is actually playing; it only releases a first line that stalled (paused /
     *     not loaded), and gives up entirely after SECOND_LINE_GIVE_UP_MS.
     *   - No voice element (the effect-pool fallback): the pool layers, so the second follows after a short beat.
     */
    const SECOND_LINE_CHECK_MS = 4000;
    const SECOND_LINE_GIVE_UP_MS = 12000;
    const SECOND_LINE_FALLBACK_GAP_MS = 1800;
    function playVoiceThen(first: string, second: string): void {
        playVoice(first);
        const channel = document.getElementById('audio_player_voice_1') as HTMLAudioElement | null;
        if (channel && (channel.muted || !(channel.volume > 0))) return; // dialogue muted by the user: neither line
        const generation = voiceGeneration;
        if (!channel) {
            setTimeout(() => { if (voiceGeneration === generation && !embeddedCampaignAudioSuppressed()) playVoice(second); }, SECOND_LINE_FALLBACK_GAP_MS);
            return;
        }
        afterVoiceLine(channel, generation, second);
    }

    /**
     * A line that must not cut the one already speaking (a concentration end, director 2026-09-22: the hit's grunt, then
     * the line). Its row lands right behind the damage row, whose voice is by then on the channel: wait for that line to
     * end, under playVoiceThen's rules. An idle channel plays it at once. `paused` — not readyState — decides "speaking",
     * so a grunt still loading its first bytes is waited for rather than cut.
     */
    function playVoiceQueued(url: string): void {
        const channel = document.getElementById('audio_player_voice_1') as HTMLAudioElement | null;
        if (!channel || channel.paused || channel.ended) { playVoice(url); return; }
        if (channel.muted || !(channel.volume > 0)) return; // dialogue muted by the user
        afterVoiceLine(channel, voiceGeneration, url);
    }

    /** Play `second` when the channel's current line (voice generation `generation`) ends; dropped if displaced. */
    function afterVoiceLine(channel: HTMLAudioElement, generation: number, second: string): void {
        const startedAt = Date.now();
        let done = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const finish = (play: boolean) => {
            if (done) return;
            done = true;
            channel.removeEventListener('ended', onEnd);
            channel.removeEventListener('error', onEnd);
            if (timer !== undefined) clearTimeout(timer);
            if (play && voiceGeneration === generation && !embeddedCampaignAudioSuppressed()) playVoice(second);
        };
        const onEnd = () => finish(voiceGeneration === generation);
        const watchdog = () => {
            if (voiceGeneration !== generation) return finish(false); // displaced: drop the pair's second line
            const stalled = channel.paused || channel.readyState < 2;
            if (stalled) return finish(true);
            if (Date.now() - startedAt >= SECOND_LINE_GIVE_UP_MS) return finish(false);
            timer = setTimeout(watchdog, SECOND_LINE_CHECK_MS); // still speaking: keep waiting for 'ended'
        };
        channel.addEventListener('ended', onEnd);
        channel.addEventListener('error', onEnd);
        timer = setTimeout(watchdog, SECOND_LINE_CHECK_MS);
    }

    /** A chat emote's bark (ft3d-chat.ts): dialogue, so it goes down the same dedicated Voice channel. */
    function playChatVoiceLine(url: string): void {
        // Chat barks are other people's campaign audio: FT3D routes only (shared/audio-surface.ts).
        if (embeddedCampaignAudioSuppressed() || !ftCampaignAudioSurface()) return;
        playVoice(url);
    }

    /** A campaign presentation cue with no roll row (the encounter ping, ft3d-ping.ts): one take on the effect pool. */
    function playCampaignCue(url: string): void {
        if (embeddedCampaignAudioSuppressed() || !ftCampaignAudioSurface()) return;
        playEffect(url);
    }

    // sfx → spread across the effect-player pool so concurrent sounds layer.
    function playEffect(file: string, seekSeconds = 0): void {
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        const dtMs = lastPlayAt ? Math.round(now - lastPlayAt) : null;

        const players = effectPlayers();
        if (!players.length) { ftDebugLog('audio', { phase: 'play', file, skipReason: 'no-effect-players' }); return; }
        // Only route to channels the user can actually hear. A muted or 0-volume channel would
        // swallow the sound silently — that layer would just vanish unpredictably. So exclude
        // them from the pool; if EVERY effect channel is silenced the user wants no SFX → skip.
        const audible = players.filter((p) => !p.muted && p.volume > 0);
        if (!audible.length) { ftDebugLog('audio', { phase: 'play', file, skipReason: 'all-effects-muted' }); return; }

        lastPlayAt = now;

        const { player, evicted } = pickPlayer(audible);
        const busyCount = audible.filter(isBusy).length; // pre-assign
        playOn(player, file, seekSeconds);
        ftDebugLog('audio', { phase: 'play', file, seekSeconds, chosenChannelId: player.id, evicted, busyCount, poolSize: audible.length, ofTotal: players.length, dtMs });
    }

    /** Play a roll frame's sound: the effect and the voice line its route resolves to in this page's catalog. */
    async function playRollSound(roll: Record<string, unknown> | undefined): Promise<void> {
        if (embeddedCampaignAudioSuppressed()) {
            ftDebugLog('audio', { phase: 'play', skipReason: 'embedded-campaign-audio-owned-by-parent' });
            return;
        }
        if (!roll?.roll_sound) return;
        // Off FT3D routes only the viewer's OWN rolls make sound; other people's campaign rolls stay silent
        // (owner 2026-09-29, shared/audio-surface.ts).
        if (!ftCampaignAudioSurface() && !ftIsViewersOwn(roll.roll_created_by_id)) {
            ftDebugLog('audio', { phase: 'play', roll_i: roll.roll_i, skipReason: 'peer-roll-off-ft3d-surface' });
            return;
        }
        // roll_id groups several saved rows; roll_i identifies the individual event.
        const rowId = String(roll.roll_i ?? '');
        if (rowId && !claimEvent(`roll:${rowId}`)) return;
        const startedAt = Date.now();
        const presentation=rollPresentationTiming(roll);
        let picked;
        try {
            // What the page holds NOW (the frame lands before its damage reaches the target's row) — a route built after the
            // catalog download on a cold page must still see this, not the updated row.
            const capture = captureRollSoundContext(roll);
            // The deadline goes INTO the resolver: a straggler past it neither plays nor claims the cast's incantation.
            picked = await resolveRollSound(roll, fetch, startedAt + LATE_MS, catalog => {
                const ctx = pageRollSoundContext(roll, catalog, capture);
                return ctx ? rollSoundRoute(roll, ctx) : null;
            });
        } catch (error) {
            ftDebugLog('audio', { phase: 'resolve', roll_i: roll.roll_i, error: String(error) });
            return;
        }
        ftDebugLog('audio', { phase: 'resolve', roll_i: roll.roll_i, sfx: picked.sfx?.audioId ?? null, voice: picked.voice?.audioId ?? null, ms: Date.now() - startedAt });
        // An applying cast's attack and damage rows can both choose its own pool. The cast owns one
        // take across those rows; distinct status/impact pools and each row's voice keep their own event.
        const castId = castCommitId(roll), used = rollSound(roll.roll_sound)?.used;
        const castPool = String(used || roll.roll_item_id || '').toUpperCase();
        if (castId && picked.sfx?.pool.toUpperCase() === castPool
            && !claimEvent(`cast:${String(roll.encounter_id ?? '').toUpperCase()}:${castId.toUpperCase()}:${String(roll.roll_by ?? '').toUpperCase()}:${castPool}`)) picked.sfx = null;
        // Audio ownership can move to the parent page while the take was being resolved.
        if (embeddedCampaignAudioSuppressed()) return;
        if (picked.voice && picked.voiceAfter) playVoiceThen(picked.voice.url, picked.voiceAfter.url);
        else if (picked.voice && picked.queue) playVoiceQueued(picked.voice.url);
        else if (picked.voice) playVoice(picked.voice.url);
        const perStepMovement = roll.roll_type_1 === 'speed_type_moved'
            && (roll.roll_type_2 === 'speed_walking' || roll.roll_type_2 === 'speed_swimming'
                || roll.roll_type_2 === 'speed_flying' || roll.roll_type_2 === 'speed_climbing')
            && document.documentElement.dataset.ft3dFootsteps === '1';
        if (picked.sfx && perStepMovement) ftDebugLog('audio', { phase: 'play', roll_i: roll.roll_i, skipReason: 'ft3d-per-step-footsteps' });
        if (picked.sfx && !perStepMovement) {
            if(presentation){
                // A projectile's hit lands when it ARRIVES (release + the VFX runtime's flight); every other shape on the release.
                const soundAt=castSoundAt(presentation,roll.roll_vfx);
                const encounter=String(roll.encounter_id||'').toUpperCase();
                // A recipe with vfxDelayMs carries an owner-tuned visual offset measured from the take's START at
                // soundAt: keep that contract (no hit pre-roll), or its visual would trail the sound it was tuned to.
                // A projectile lines up the take's IMPACT (past a launch whoosh, effect-onset takeImpactSeconds) with its arrival.
                const alignImpact=presentation.soundAt===undefined&&projectileImpactMs(roll.roll_vfx)!==null;
                await playCastEffect(picked.sfx.url,soundAt,encounter,roll.roll_i,presentation.soundAt===undefined,presentation.releaseAt,alignImpact,roll.roll_id ?? rowId);
            }else if(['attack_ability','opportunity_attack_ability'].includes(String(roll.roll_item_id_item_type||'').toLowerCase())&&roll.roll_id&&roll.roll_by
                // Only the rows that swing (damage landed / attack missed) — contests and bookkeeping keep their own beat.
                &&(String(roll.roll_type_1||'')===((window as any).ROLLTYPE_CATALOG?.damage_roll?.item_id??null)||/_attack_missed$/.test(String(roll.roll_type_1||'')))){
                // An attack ability's hit/whiff lands on its swing's impact — the same shared clock the scene retimes the
                // swing onto (damage-feedback). A standee attacker's clock is "now".
                await playCastEffect(picked.sfx.url,attackImpactAt(String(roll.roll_id),String(roll.roll_by)),String(roll.encounter_id||'').toUpperCase(),roll.roll_i,true,undefined,false,roll.roll_id);
            }else playEffect(picked.sfx.url);
        }
    }

    /** Cast SFX device: unlocked by the viewer's first gesture (top window only — embedded scenes defer to it). */
    let castAudio: AudioContext | null = null;
    if (window.parent === window) for (const type of ['pointerdown', 'keydown']) document.addEventListener(type, () => {
        try { castAudio ??= new AudioContext(); void castAudio.resume().catch(() => {}); } catch { /* no Web Audio */ }
    }, { passive: true });
    const castPlayed = new Map<string, number>();
    const castReceipt = (row: Record<string, unknown>) => {
        ftDebugLog('audio', { phase: 'cast-sync', ...row });
        const receipts = ((window as any).__ftCastSyncAudio ??= []) as unknown[]; receipts.push(row); if (receipts.length > 48) receipts.shift();
    };

    /**
     * A cast's SFX with its HIT at `soundAt` — the release, or a projectile's arrival (docs/FT3D_CAST_SYNC_AUDIT.md). The decoded take is scheduled
     * through Web Audio at the exact offset (a late arrival starts INTO the buffer, so the hit is never later than
     * now); without an unlocked context it falls back to an <audio> channel, started early by the hit, and says so.
     */
    async function playCastEffect(url: string, soundAt: number, encounter: string, rollI: unknown, alignHit = true, releaseAt?: number, alignImpact = false, eventId: unknown = rollI, actionCue = false): Promise<void> {
        const live = () => {
            const current = String((window as any).ftGetActiveEncounterId?.() || '').toUpperCase();
            return !embeddedCampaignAudioSuppressed() && !(current && encounter && current !== encounter)
                && (actionCue || Date.now() <= soundAt + 500);
        };
        let take: EffectTake | null = null;
        try { take = await Promise.race([effectTake(url), new Promise<null>(resolve => window.setTimeout(() => resolve(null), 1500))]); } catch { take = null; }
        if (take && !alignHit) take = { ...take, hit: 0 };
        else if (take && alignImpact) take = { ...take, hit: take.impact };
        if (!live()) { castReceipt({ roll_i: rollI, url, soundAt, path: 'skipped' }); return; }
        // Two darts drawing the same take on one release would only double its level. Keyed on the RELEASE when known:
        // a projectile's hit sits at its arrival, and two cues of one release must not escape the dedupe by arriving apart.
        const key = `${encounter}:${eventId}:${url}@${releaseAt ?? soundAt}`;
        if (castPlayed.has(key)) { castReceipt({ roll_i: rollI, url, soundAt, path: 'duplicate' }); return; }
        castPlayed.set(key, soundAt); if (castPlayed.size > 64) castPlayed.delete(castPlayed.keys().next().value!);
        const ctx = castAudio;
        if (take && ctx?.state === 'running') {
            const source = ctx.createBufferSource(), gain = ctx.createGain();
            let release: (() => void) | undefined, removeControl: (() => void) | undefined;
            const stop = () => { try { source.stop(); } catch { /* not started */ } source.disconnect(); gain.disconnect(); release?.(); removeControl?.(); };
            const slot = reserveEffectDevice(stop);
            if (slot) {
                release = slot.release;
                const controls = () => { gain.gain.value = slot.player.muted ? 0 : slot.player.volume; };
                slot.player.addEventListener('volumechange', controls); controls();
                removeControl = () => slot.player.removeEventListener('volumechange', controls);
                source.buffer = take.buffer; source.connect(gain); gain.connect(ctx.destination);
                source.onended = () => { source.disconnect(); gain.disconnect(); release?.(); removeControl?.(); };
                // Seconds until buffer offset 0 must be HEARD; output latency is paid before that.
                const lead = (soundAt - take.hit * 1000 - Date.now()) / 1000 - (ctx.outputLatency || ctx.baseLatency || 0);
                // A cold committed cue still plays in full after the swing clock.
                // Seeking past a short whiff would start a silent source.
                source.start(ctx.currentTime + Math.max(0, lead), actionCue && Date.now() > soundAt ? 0 : Math.max(0, -lead));
                // Re-check at the moment it becomes audible: an encounter switch or embedded hand-off in between stops it.
                window.setTimeout(() => { if (!live()) stop(); }, Math.max(0, lead * 1000));
                castReceipt({ roll_i: rollI, url, hit: take.hit, soundAt, releaseAt, path: 'webaudio', lead, startAt: soundAt - take.hit * 1000, channel: slot.player.id });
                return;
            }
        }
        // Fallback: an <audio> channel, loaded from the warm HTTP cache when the analysis fetched it.
        const hit = take?.hit ?? 0;
        const preroll = Math.min(hit, MAX_EFFECT_PREROLL_SECONDS, Math.max(0, (soundAt - Date.now()) / 1000));
        const seek = Math.max(0, hit - preroll);
        castReceipt({ roll_i: rollI, url, hit, soundAt, releaseAt, path: take ? 'element' : 'element-unanalysed', preroll, seek, startAt: soundAt - preroll * 1000 });
        // Recompute seek when the timer fires; a late committed cue starts in full.
        window.setTimeout(() => {
            if (live()) playEffect(url, actionCue && Date.now() > soundAt ? 0 : Math.max(0, hit - (soundAt - Date.now()) / 1000));
        }, Math.max(0, soundAt - preroll * 1000 - Date.now()));
    }

    const playedEvents = new Set<string>();
    function claimEvent(key: string): boolean {
        if (playedEvents.has(key)) return false;
        playedEvents.add(key);
        if (playedEvents.size > 400) for (const old of [...playedEvents].slice(0, 100)) playedEvents.delete(old);
        return true;
    }
    /**
     * A client-truth action's sound, ONCE per operation: the actor calls this the instant it applies its own result
     * (the take it picked rides the command), a peer on the first frame of that operation it sees. Nothing about it
     * waits on, or is stored in, the rolls table (owner 2026-09-10).
     */
    /** Cue slots that sound when the weapon CONNECTS (cast-sync #8c); the dice clatter and reactions stay at send. */
    const IMPACT_SLOTS = new Set(['hit', 'whiff', 'rider']);
    async function playActionSound(operationId: string, sound: unknown, opts: { peer?: boolean; hitAt?: number } = {}): Promise<void> {
        const op = String(operationId ?? '').toUpperCase();
        if (!op || !sound) return;
        // A peer's action (the WS subscriber path) is campaign audio: FT3D routes only (shared/audio-surface.ts).
        // The actor's own click keeps its sound wherever it happens.
        if (opts.peer && !ftCampaignAudioSurface()) return;
        if (!claimEvent(`action:${op}`)) return;
        if (embeddedCampaignAudioSuppressed()) return;
        const startedAt = Date.now();
        // Effects and the voice line resolve independently: a slow voice-shard download never holds the effects past LATE_MS.
        const play = async (part: 'effects' | 'voice'): Promise<void> => {
            let takes: Awaited<ReturnType<typeof actionSoundTakes>> = [];
            try { takes = await actionSoundTakes(sound, fetch, part); } catch { return; }
            if (!takes.length || Date.now() - startedAt > LATE_MS || embeddedCampaignAudioSuppressed()) return;
            ftDebugLog('audio', { phase: 'action', part, operationId: op, files: takes.map(t => t.url) });
            const heard = new Set<string>();
            for (const t of takes) {
                if (heard.has(t.url)) continue;
                heard.add(t.url);
                if (t.voice) playVoice(t.url);
                // Ready impact takes share the swing clock; cold committed cues still play once.
                else if (opts.hitAt !== undefined && t.slot && IMPACT_SLOTS.has(t.slot)) void playCastEffect(t.url, opts.hitAt, '', op, true, undefined, false, op, true);
                else playEffect(t.url);
            }
        };
        await Promise.all([play('effects'), play('voice')]);
    }

    // Warm the catalog while the page is idle so the first roll's sound is not waiting on the download — and the voice
    // shards of the encounter's voiced creatures, so an action the actor resolves itself can speak the same instant.
    try {
        const warmVoices = () => { void loadAudioCatalog().then(c => { if (c) warmVoiceShards(encounterVoiceSlugs(c)); }); };
        const warm = () => { warmVoices(); };
        const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
        if (idle) idle(warm, { timeout: 4000 }); else setTimeout(warm, 1500);
        window.addEventListener('ft:encounter-state-received', () => { if (peekAudioCatalog()) warmVoices(); });
    } catch { /* warming is an optimisation only */ }
    return { reserveEffectDevice, playChatVoiceLine, playCampaignCue, playRollSound, playActionSound };
}
